import { create } from "zustand";
import { StateCreator } from "zustand/vanilla";
import {
  OrderStatusSetting,
  MerchantOrder,
  OrderItem
} from "../../types/order";

interface OrdersState {
  // Order status settings
  orderStatusSettings: OrderStatusSetting[];
  lastDivId: string;

  // Orders data
  orders: Map<string, OrderItem[]>;

  // UI state
  visibleViews: string[];
  activeView: string;
  isErrorModalOpen: boolean;
  errorMessage: string;

  // Console state
  consoleRefreshCount: number;
  newOrderIds: string[];
  isPreLoad: boolean;

  // Merchant info
  businessId: string;
  encryptedMerchantId: string;
  domainNameApi: string;
  businessName: string;

  // Loading states
  isLoading: boolean;
  isProcessingOrder: boolean;
  hasInteracted: boolean;
  audio: HTMLAudioElement | null;

  setOrderStatusSettings: (settings: OrderStatusSetting[]) => void;
  setLastDivId: (lastDivId: string) => void;
  setOrders: (statusId: string, orders: OrderItem[]) => void;
  addOrder: (statusId: string, order: OrderItem) => void;
  removeOrder: (statusId: string, orderId: string) => void;
  updateOrderStatus: (
    orderId: string,
    oldStatus: string,
    newStatus: string,
    buttonText: string
  ) => void;
  setOrderProcessing: (
    orderId: string,
    statusId: string,
    isProcessing: boolean
  ) => void;
  setVisibleViews: (views: string[]) => void;
  setActiveView: (view: string) => void;
  setErrorModal: (isOpen: boolean, message?: string) => void;
  setConsoleRefreshCount: (count: number) => void;
  addNewOrderId: (orderId: string) => void;
  setPreLoad: (isPreLoad: boolean) => void;
  setMerchantInfo: (
    businessId: string,
    encryptedMerchantId: string,
    domainNameApi: string,
    businessName: string
  ) => void;
  setLoading: (isLoading: boolean) => void;
  setProcessingOrder: (isProcessing: boolean) => void;
  reset: () => void;
  setUserInteracted: (audio: HTMLAudioElement) => void;
  setBusinessId: (businessId: string) => void;
  setBusinessName: (businessName: string) => void;
  debugBusinessId: () => void;
}

const INITIAL_STATE = {
  orderStatusSettings: [],
  lastDivId: "",
  orders: new Map(),
  visibleViews: [],
  activeView: "All",
  isErrorModalOpen: false,
  errorMessage: "",
  consoleRefreshCount: 0,
  newOrderIds: [],
  isPreLoad: false,
  businessId: "",
  encryptedMerchantId: "",
  domainNameApi: "",
  businessName: "",
  isLoading: true,
  isProcessingOrder: false,
  hasInteracted: false,
  audio: null
};

const store: StateCreator<OrdersState> = (set, get) => ({
  ...INITIAL_STATE,

  // Actions
  setOrderStatusSettings: (settings: OrderStatusSetting[]) => {
    set({ orderStatusSettings: settings });
  },

  setLastDivId: (lastDivId: string) => {
    set({ lastDivId });
  },

  setOrders: (statusId: string, orders: OrderItem[]) => {
    const newOrders = new Map(get().orders);
    newOrders.set(statusId, orders);
    set({ orders: newOrders });
  },

  addOrder: (statusId: string, order: OrderItem) => {
    const currentOrders = get().orders.get(statusId) || [];
    const newOrders = new Map(get().orders);
    newOrders.set(statusId, [order, ...currentOrders]);
    set({ orders: newOrders });
  },

  removeOrder: (statusId: string, orderId: string) => {
    const currentOrders = get().orders.get(statusId) || [];
    const newOrders = new Map(get().orders);
    newOrders.set(
      statusId,
      currentOrders.filter((order) => order.id !== orderId)
    );
    set({ orders: newOrders });
  },

  updateOrderStatus: (
    orderId: string,
    oldStatus: string,
    newStatus: string,
    buttonText: string
  ) => {
    const oldOrders = get().orders.get(oldStatus) || [];
    const newOrders = new Map(get().orders);

    // Remove from old status
    newOrders.set(
      oldStatus,
      oldOrders.filter((order) => order.id !== orderId)
    );

    // Add to new status
    const orderToMove = oldOrders.find((order) => order.id === orderId);
    if (orderToMove) {
      const newStatusOrders = get().orders.get(newStatus) || [];
      newStatusOrders.push({
        ...orderToMove,
        orderStatus: newStatus,
        buttonText,
        isProcessing: false
      });
      newOrders.set(newStatus, newStatusOrders);
    }

    set({ orders: newOrders });
  },

  setOrderProcessing: (
    orderId: string,
    statusId: string,
    isProcessing: boolean
  ) => {
    const currentOrders = get().orders.get(statusId) || [];
    const newOrders = new Map(get().orders);
    newOrders.set(
      statusId,
      currentOrders.map((order) =>
        order.id === orderId ? { ...order, isProcessing } : order
      )
    );
    set({ orders: newOrders });
  },

  setVisibleViews: (views: string[]) => {
    set({ visibleViews: views });
  },

  setActiveView: (view: string) => {
    set({ activeView: view });
  },

  setErrorModal: (isOpen: boolean, message: string = "") => {
    set({ isErrorModalOpen: isOpen, errorMessage: message });
  },

  setConsoleRefreshCount: (count: number) => {
    set({ consoleRefreshCount: count });
  },

  addNewOrderId: (orderId: string) => {
    const currentIds = get().newOrderIds;
    if (!currentIds.includes(orderId)) {
      set({ newOrderIds: [...currentIds, orderId] });
    }
  },

  setPreLoad: (isPreLoad: boolean) => {
    set({ isPreLoad });
  },

  setMerchantInfo: (
    businessId: string,
    encryptedMerchantId: string,
    domainNameApi: string,
    businessName: string
  ) => {
    set({ businessId, encryptedMerchantId, domainNameApi, businessName });
  },

  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  setProcessingOrder: (isProcessing: boolean) => {
    set({ isProcessingOrder: isProcessing });
  },

  reset: () => {
    set(INITIAL_STATE);
  },

  setUserInteracted: (audio: HTMLAudioElement) => {
    set({ hasInteracted: true, audio: audio });
  },

  setBusinessId: (businessId: string) => {
    set({ businessId });
  },

  setBusinessName: (businessName: string) => {
    set({ businessName });
  },

  debugBusinessId: () => {
    console.log("Current business ID:", get().businessId);
  }
});

export const useOrdersStore = create<OrdersState>(store);
